import { ElementRef } from '@angular/core';
import {
  NavigationItemMovement,
  NavigationItemMovingStrategies
} from '@/app/platform/platform-shared/navigation-item-tree/navigation-item-moving-strategies';
import { FlatTreeControl } from '@angular/cdk/tree';
import Morphism from 'morphism';
import { FlatNode } from '@/app/platform/platform-shared/navigation-item-tree/types';
import { NavigationItemData } from '@/app/platform/platform-shared/models/navigation-item.data';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';

const dragNodeExpandOverWaitTimeMs = 100;

export class NavigationItemDragger {
  private dragNodeExpandOverNode: any;
  private dragNodeExpandOverTime: number;
  moveArea: NavigationItemMovement;

  constructor(
    private readonly emptyItem: ElementRef,
    private readonly treeControl: FlatTreeControl<NavigationItemNode<NavigationItemEntity>, string>,
    private readonly navigationItemMovingStrategies: NavigationItemMovingStrategies
  ) {}

  dragNode?: NavigationItemNode<NavigationItemEntity>;

  startDrag(event: DragEvent, node: NavigationItemNode<NavigationItemEntity>): void {
    event.dataTransfer.setData('foo', 'bar');
    event.dataTransfer.setDragImage(this.emptyItem.nativeElement, 0, 0);
    this.dragNode = node;
  }

  dragOver(event: any, node: NavigationItemNode<NavigationItemEntity>): void {
    if (node === this.dragNodeExpandOverNode) {
      if (this.dragNode !== node && !this.treeControl.isExpanded(node)) {
        if (new Date().getTime() - this.dragNodeExpandOverTime > dragNodeExpandOverWaitTimeMs) {
          this.treeControl.expand(node);
        }
      }
    } else {
      this.dragNodeExpandOverNode = node;
      this.dragNodeExpandOverTime = new Date().getTime();
    }

    this.decideMovement(event, node);
  }

  moveNode(targetNode: NavigationItemNode<NavigationItemEntity>): NavigationItemEntity {
    const movedItem = this.copyDraggedNode();
    const targetItem = targetNode.navigationItem;
    const move = this.navigationItemMovingStrategies[this.moveArea];
    return move(movedItem, targetItem);
  }

  endDrag(): void {
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  isSameAsDraggedNode(targetNode: NavigationItemNode<NavigationItemEntity>): boolean {
    return targetNode === this.dragNode;
  }

  get isDraggedAbove(): (node: NavigationItemNode<NavigationItemEntity>) => boolean {
    return node => this.moveArea === 'above' && this.isTargetNode(node);
  }

  get isDraggedBelow(): (node: NavigationItemNode<NavigationItemEntity>) => boolean {
    return node => this.moveArea === 'below' && this.isTargetNode(node);
  }

  get isDraggedCenter(): (node: NavigationItemNode<NavigationItemEntity>) => boolean {
    return node =>
      (this.moveArea === 'center' || node.navigationItem.isRoot) && this.isTargetNode(node);
  }

  private isTargetNode(node: NavigationItemNode<NavigationItemEntity>): boolean {
    return this.dragNodeExpandOverNode === node;
  }

  private copyDraggedNode(): NavigationItemEntity {
    return Morphism.map(NavigationItemEntity, this.dragNode.navigationItem);
  }

  private decideMovement(event: any, node: NavigationItemNode<NavigationItemEntity>) {
    if (node.navigationItem.isRoot) {
      this.moveArea = 'center';
      return;
    }
    const percentageX = event.offsetX / event.target.clientWidth;
    const percentageY = event.offsetY / event.target.clientHeight;
    if (percentageY < 0.25) {
      this.moveArea = 'above';
    } else if (percentageY > 0.75) {
      this.moveArea = 'below';
    } else {
      this.moveArea = 'center';
    }
  }
}

export class NavigationItemNode<T extends NavigationItemData> implements FlatNode {
  constructor(public name: string, public id: string, public navigationItem: T) {}

  level: number;
  expandable: boolean;
  order: number;
  editing = false;

  entered = false;
}
